import {
	Alert,
	Button,
	Card,
	Grid,
	Group,
	SegmentedControl,
	Stack,
	Text,
	Title,
} from "@mantine/core";
import { useReducedMotion } from "@mantine/hooks";
import { IconAlertCircle, IconDeviceFloppy } from "@tabler/icons-react";
import { useState } from "react";
import { useSnapshot } from "valtio";
import { toAssistantLang } from "@/components/assistant/assistant.logic";
import { assistantOrbTexts } from "@/locales/assistant-orb";
import { fillStyleText } from "@/locales/assistant-style";
import { i18nStore } from "@/store/i18n";
import {
	type AssistantOrbConfig,
	ORB_SIZES,
	type OrbSize,
} from "@/types/ai-assistant-orb";
import { OrbColorPicker } from "./orb-color-picker";
import { OrbParamsEditor } from "./orb-params";
import { OrbPreview } from "./orb-preview";
import { OrbVariantGrid } from "./orb-variant-grid";
import { orbHasChanges, useAssistantOrb } from "./use-assistant-orb";

/**
 * Bagian "Logo Jenna": pilih bentuk → setel warna & parameter → pratinjau
 * langsung → simpan. Perubahan hanya mengubah state lokal sampai Simpan
 * ditekan, jadi admin bisa mencoba-coba tanpa mengubah tampilan produksi.
 */
export function OrbSection({
	assistantName,
	disabled,
}: {
	assistantName: string;
	disabled: boolean;
}) {
	const { lang: uiLang, animasiTransisi } = useSnapshot(i18nStore);
	const text = assistantOrbTexts[toAssistantLang(uiLang)];
	const reducedMotion = useReducedMotion();
	const orb = useAssistantOrb();
	const [previewLight, setPreviewLight] = useState(false);
	const [busy, setBusy] = useState(false);
	const [notice, setNotice] = useState<string | null>(null);
	const [failure, setFailure] = useState<string | null>(null);
	const name = { name: assistantName };

	const locked = disabled || orb.loading || orb.unavailable || busy;
	const dirty = orbHasChanges(orb.draft, orb.saved);

	const patch = (changes: Partial<AssistantOrbConfig>) => {
		setNotice(null);
		orb.setDraft({ ...orb.draft, ...changes });
	};

	const run = async (action: () => Promise<unknown>, done: string) => {
		setBusy(true);
		setFailure(null);
		setNotice(null);
		try {
			await action();
			setNotice(done);
		} catch (err) {
			setFailure(
				`${text.saveFailed}: ${err instanceof Error ? err.message : String(err)}`,
			);
		} finally {
			setBusy(false);
		}
	};

	return (
		<Card withBorder radius="md" p="lg" data-orb-section>
			<Title order={4} mb={4}>
				{fillStyleText(text.title, name)}
			</Title>
			<Text size="sm" c="dimmed" mb="md">
				{fillStyleText(text.description, name)}
			</Text>

			<Stack gap="md">
				{orb.unavailable && <Alert color="blue">{text.unavailable}</Alert>}
				{(orb.error || failure) && (
					<Alert
						color="red"
						icon={<IconAlertCircle size={16} />}
						data-orb-error
					>
						{failure ?? text.loadFailed}
					</Alert>
				)}
				{(reducedMotion || !animasiTransisi) && (
					<Alert color="gray" data-orb-reduced-motion>
						{text.reducedMotion}
					</Alert>
				)}

				<Text size="sm" fw={600}>
					{text.shape}
				</Text>
				<OrbVariantGrid
					config={orb.draft}
					text={text}
					disabled={locked}
					forceLight={previewLight}
					onSelect={(variant) => patch({ variant })}
				/>

				<div>
					<Text size="sm" fw={600} mb="xs">
						{text.size}
					</Text>
					<SegmentedControl
						value={orb.draft.size}
						disabled={locked}
						data-orb-size
						onChange={(value) => patch({ size: value as OrbSize })}
						data={ORB_SIZES.map((size) => ({
							value: size,
							label: text.sizeNames[size] ?? size,
						}))}
					/>
				</div>

				<Grid gutter="lg">
					<Grid.Col span={{ base: 12, md: 6 }}>
						<Text size="sm" fw={600} mb="xs">
							{text.preview}
						</Text>
						<OrbPreview
							config={orb.draft}
							name={assistantName}
							text={text}
							light={previewLight}
							onThemeChange={setPreviewLight}
						/>
					</Grid.Col>
					<Grid.Col span={{ base: 12, md: 6 }}>
						<Stack gap="lg">
							<div>
								<Text size="sm" fw={600} mb="xs">
									{text.colors}
								</Text>
								<OrbColorPicker
									config={orb.draft}
									text={text}
									disabled={locked}
									onChange={patch}
								/>
							</div>
							<div>
								<Text size="sm" fw={600} mb="xs">
									{text.settings}
								</Text>
								<OrbParamsEditor
									config={orb.draft}
									text={text}
									disabled={locked}
									onChange={(params) => patch({ params })}
								/>
							</div>
						</Stack>
					</Grid.Col>
				</Grid>

				{notice && (
					<Text size="sm" c="green" data-orb-notice>
						{notice}
					</Text>
				)}

				<Group>
					<Button
						leftSection={<IconDeviceFloppy size={16} />}
						disabled={locked || !dirty}
						loading={busy}
						onClick={() => run(orb.save, text.saved)}
						data-orb-save
					>
						{text.save}
					</Button>
					{dirty && (
						<Button
							variant="subtle"
							color="gray"
							disabled={busy}
							onClick={orb.revert}
							data-orb-cancel
						>
							{text.cancel}
						</Button>
					)}
					<Button
						variant="light"
						color="red"
						disabled={locked}
						onClick={() => run(orb.reset, text.resetDone)}
						data-orb-reset
					>
						{text.reset}
					</Button>
				</Group>
			</Stack>
		</Card>
	);
}
